/**
 * LanguageToggle — English ↔ Spanish pill for the site header.
 *
 * Uses the Google Translate Element (loaded invisibly in index.html) as
 * the translation engine. We drive it via the `googtrans` cookie because
 * that's the value the widget reads on every page load, so the language
 * choice persists across navigations without every page needing its own
 * translation dictionary.
 *
 * Rendering & behaviour:
 *   - Pill sits at the top-right of the header on every marketing +
 *     patient-side page (whichever surface renders <LanguageToggle/>).
 *   - Clicking "ES" sets the cookie + forces the widget to switch;
 *     clicking "EN" clears the cookie and reloads back to the source
 *     English page.
 *   - The stock Google top-banner ("This page has been translated…") is
 *     hidden via CSS in index.css so the app chrome stays clean.
 *   - Brand-critical strings should be marked `translate="no"` on the
 *     element (or wrap in <span translate="no">…</span>) — the widget
 *     honours that per HTML5 spec.
 */
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";

const COOKIE_NAME = "googtrans";
const STORAGE_KEY = "glpflo_lang_v1";

function readCookie(name) {
  const match = document.cookie.split("; ").find((r) => r.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=")[1]) : "";
}

function writeCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  // Set on both the current host AND the parent domain so Google's
  // widget picks it up regardless of subdomain (preview vs prod).
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length >= 2) {
    const parent = "." + parts.slice(-2).join(".");
    document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=${parent}`;
  }
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length >= 2) {
    const parent = "." + parts.slice(-2).join(".");
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parent}`;
  }
}

function detectLang() {
  // The Google cookie is `/en/es` when Spanish is active, empty otherwise.
  const raw = readCookie(COOKIE_NAME);
  if (raw.startsWith("/en/es")) return "es";
  return "en";
}

export default function LanguageToggle({ compact = false }) {
  const [lang, setLang] = useState(detectLang());

  useEffect(() => {
    // If the user's stored preference is Spanish but the cookie is stale
    // (e.g. Google set/wiped it during a redirect), re-apply on mount.
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "es" && detectLang() !== "es") {
      writeCookie(COOKIE_NAME, "/en/es");
      setLang("es");
    }
  }, []);

  const switchTo = (target) => {
    if (target === lang) return;
    localStorage.setItem(STORAGE_KEY, target);
    if (target === "es") {
      writeCookie(COOKIE_NAME, "/en/es");
    } else {
      deleteCookie(COOKIE_NAME);
    }
    // Best-effort sync to the logged-in user's profile so their inbox
    // matches — anonymous callers 401 harmlessly; we don't block the
    // page reload on it.
    try {
      import("@/lib/api").then(({ default: API }) => {
        API.post("/auth/preferred-language", { language: target }).catch(() => {});
      });
    } catch (_) { /* non-fatal */ }
    // Google Translate only re-reads the cookie on full navigation — a
    // hard reload guarantees the widget picks up the new target.
    window.location.reload();
  };

  const base = compact
    ? "text-xs px-2.5 py-1"
    : "text-sm px-3 py-1.5";
  const activeCls = "bg-purple-600 text-white shadow-sm";
  const inactiveCls = "bg-transparent text-slate-600 hover:text-slate-900";

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border-2 border-purple-300 bg-white px-1.5 py-1 shadow-md hover:shadow-lg transition-shadow"
      data-testid="language-toggle"
      translate="no"
      aria-label="Language selector — English or Spanish"
    >
      <Globe className={compact ? "w-3.5 h-3.5 text-purple-500 ml-1" : "w-4 h-4 text-purple-500 ml-1"} />
      <button
        type="button"
        onClick={() => switchTo("en")}
        className={`${base} rounded-full font-bold tracking-wide transition-colors ${lang === "en" ? activeCls : inactiveCls}`}
        data-testid="lang-toggle-en"
        aria-pressed={lang === "en"}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => switchTo("es")}
        className={`${base} rounded-full font-bold tracking-wide transition-colors ${lang === "es" ? activeCls : inactiveCls}`}
        data-testid="lang-toggle-es"
        aria-pressed={lang === "es"}
      >
        ES
      </button>
    </div>
  );
}
